import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { PAYMENTS_LIST_PAGE_STYLES, paymentStatusLabelKey } from './payments-list-page.utils';
import { FinanceApiService, PlatformCustomerRecord } from '../finance/finance-api.service';
import { LoanRepaymentRow, LoanRow, PaymentsAdminService } from './payments-admin.service';

@Component({
  standalone: true,
  selector: 'ain-payments-lending',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    SelectModule,
    TagModule,
    AinCanAccessDirective,
    CustomerSelectorComponent,
    CurrencySelectorComponent,
    AinAmountPipe
  ],
  template: `
    <ain-page [eyebrow]="t('payments.eyebrow')" [title]="t('payments.lending.title')" [description]="t('payments.lending.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'payments.lending.help.page.title'"
        [descriptionKey]="'payments.lending.help.page.description'"
        [bulletKeys]="[
          'payments.lending.help.page.bullets.create',
          'payments.lending.help.page.bullets.loans',
          'payments.lending.help.page.bullets.repayments'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('payments.lending.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('payments.lending.unavailable')" [message]="error() ?? ''" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button
              pButton
              type="button"
              [label]="t('common.create')"
              ainCanAccess
              [ainCanAccessPermission]="'payments.lending'"
              (click)="openCreateDialog()">
            </button>
            <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="reload()"></button>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('payments.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'payments.lending.help.filters.title'"
                [descriptionKey]="'payments.lending.help.filters.description'" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('payments.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('payments.lending.filters.status') }}</span>
                <p-select
                  [options]="loanStatusFilterOptions()"
                  optionValue="value"
                  [(ngModel)]="loanStatusFilter"
                  name="loanStatusFilter"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            </div>
          </details>

          <div class="section-title">
            <h3>{{ t('payments.lending.loans') }}</h3>
            <ain-info-hint
              [titleKey]="'payments.lending.help.loans.title'"
              [descriptionKey]="'payments.lending.help.loans.description'" />
          </div>
          @if (filteredLoans().length === 0) {
            <ain-state type="empty" [title]="t('payments.lending.states.emptyLoans')" [message]="t('payments.lending.states.emptyLoansMessage')" />
          } @else {
            <p-table [value]="filteredLoans()" dataKey="id" [paginator]="true" [rows]="10">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('payments.lending.customerId') }}</th>
                  <th>{{ t('payments.lending.principal') }}</th>
                  <th>{{ t('payments.lending.term') }}</th>
                  <th>{{ t('columns.status') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>
                    <div class="customer-cell">
                      <span class="customer-name">{{ customerLabel(row.customerId) }}</span>
                      @if (customerCode(row.customerId)) {
                        <span class="customer-code">{{ customerCode(row.customerId) }}</span>
                      }
                      <p-tag [value]="customerStatusLabel(row.customerId)" [severity]="customerStatusSeverity(row.customerId)"></p-tag>
                    </div>
                  </td>
                  <td>{{ row.principalAmount | ainAmount }} {{ row.currencyCode }}</td>
                  <td>{{ row.termMonths }}</td>
                  <td><p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)"></p-tag></td>
                </tr>
              </ng-template>
            </p-table>
          }

          <div class="section-title">
            <h3>{{ t('payments.lending.repayments') }}</h3>
            <ain-info-hint
              [titleKey]="'payments.lending.help.repayments.title'"
              [descriptionKey]="'payments.lending.help.repayments.description'" />
          </div>
          @if (filteredRepayments().length === 0) {
            <ain-state type="empty" [title]="t('payments.lending.states.emptyRepayments')" [message]="t('payments.lending.states.emptyRepaymentsMessage')" />
          } @else {
            <p-table [value]="filteredRepayments()" dataKey="id" [paginator]="true" [rows]="10">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('payments.lending.idempotencyKey') }}</th>
                  <th>{{ t('payments.lending.principal') }}</th>
                  <th>{{ t('columns.status') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.idempotencyKey }}</td>
                  <td>{{ row.amount | ainAmount }} {{ row.currencyCode }}</td>
                  <td>{{ repaymentStatusLabel(row) }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (actionError()) { <p class="error">{{ actionError() }}</p> }
        </section>
      }

      <p-dialog
        [header]="t('payments.lending.createTitle')"
        [modal]="true"
        [(visible)]="createDialogVisible"
        [style]="{ width: '640px' }">
        <div class="dialog-grid">
          <label class="wide">
            <span class="label-row">
              {{ t('payments.lending.customerId') }}
              <ain-info-hint
                [titleKey]="'payments.lending.help.create.title'"
                [descriptionKey]="'payments.lending.help.create.description'" />
            </span>
            <ain-customer-selector [(ngModel)]="createModel.customerId" name="createCustomerId" />
          </label>
          <label>
            <span>{{ t('payments.lending.externalReference') }}</span>
            <input pInputText [(ngModel)]="createModel.externalReference" name="createExternalReference" />
          </label>
          <label>
            <span>{{ t('payments.lending.idempotencyKey') }}</span>
            <input pInputText [(ngModel)]="createModel.idempotencyKey" name="createIdempotencyKey" />
          </label>
          <label>
            <span>{{ t('payments.lending.principal') }}</span>
            <input pInputText type="number" [(ngModel)]="createModel.principalAmount" name="createPrincipalAmount" />
          </label>
          <label>
            <span>{{ t('payments.lending.rate') }}</span>
            <input pInputText type="number" [(ngModel)]="createModel.annualRatePercent" name="createAnnualRatePercent" />
          </label>
          <label>
            <span>{{ t('payments.lending.term') }}</span>
            <input pInputText type="number" [(ngModel)]="createModel.termMonths" name="createTermMonths" />
          </label>
          <label>
            <span>{{ t('columns.currency') }}</span>
            <ain-currency-selector [(ngModel)]="createModel.currencyCode" name="createCurrencyCode" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="creating()" (click)="createLoan()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .section-title { margin: 12px 0 8px; font-size: 1rem; }
    .label-row { display: inline-flex; align-items: center; gap: 6px; }
    .customer-cell { display: flex; flex-direction: column; gap: 2px; }
    .customer-name { font-weight: 600; }
    .customer-code { font-size: 12px; color: #64748b; }
    ${PAYMENTS_LIST_PAGE_STYLES}
  `]
})
export class PaymentsLendingComponent implements OnInit {
  private readonly api = inject(PaymentsAdminService);
  private readonly financeApi = inject(FinanceApiService);
  private readonly localizationService = inject(LocalizationService);

  readonly loans = signal<LoanRow[]>([]);
  readonly repayments = signal<LoanRepaymentRow[]>([]);
  readonly platformCustomers = signal<Map<string, PlatformCustomerRecord>>(new Map());
  readonly loading = signal(true);
  readonly creating = signal(false);
  readonly error = signal<string | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly search = signal('');
  loanStatusFilter: string | null = null;
  createDialogVisible = false;
  createModel = {
    customerId: '',
    externalReference: '',
    idempotencyKey: '',
    principalAmount: 0,
    annualRatePercent: 12,
    termMonths: 12,
    currencyCode: 'IRR'
  };

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.actionError.set(null);

    let loaded = 0;
    const markDone = (): void => {
      loaded += 1;
      if (loaded >= 2) this.loading.set(false);
    };

    this.api.listLoans().subscribe({
      next: rows => {
        this.loans.set(rows);
        this.resolveLoanCustomers(rows);
        markDone();
      },
      error: () => {
        this.error.set(this.t('payments.lending.errorLoad'));
        this.loading.set(false);
      }
    });

    this.api.listRepayments().subscribe({
      next: rows => {
        this.repayments.set(rows);
        markDone();
      },
      error: () => {
        this.error.set(this.t('payments.lending.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  private resolveLoanCustomers(loans: LoanRow[]): void {
    const ids = [...new Set(loans.map(row => row.customerId?.trim()).filter((id): id is string => !!id))];
    for (const customerId of ids) {
      const cacheKey = customerId.toLowerCase();
      if (this.platformCustomers().has(cacheKey)) {
        continue;
      }

      this.financeApi.getPlatformCustomer(customerId).subscribe({
        next: customer => {
          this.platformCustomers.update(map => {
            const next = new Map(map);
            next.set(customer.id.toLowerCase(), customer);
            return next;
          });
        }
      });
    }
  }

  private platformCustomer(customerId: string): PlatformCustomerRecord | null {
    return this.platformCustomers().get(customerId.trim().toLowerCase()) ?? null;
  }

  loanStatusFilterOptions(): { value: string; labelKey: string }[] {
    const statuses = [...new Set(this.loans().map(row => row.status).filter(Boolean))].sort();
    return statuses.map(value => ({ value, labelKey: paymentStatusLabelKey(value) }));
  }

  filteredLoans(): LoanRow[] {
    const value = this.search().trim().toLowerCase();
    return this.loans()
      .filter(row => !this.loanStatusFilter || row.status === this.loanStatusFilter)
      .filter(row =>
        !value ||
        this.customerLabel(row.customerId).toLowerCase().includes(value) ||
        this.customerCode(row.customerId).toLowerCase().includes(value) ||
        row.externalReference?.toLowerCase().includes(value) ||
        this.statusLabel(row.status).toLowerCase().includes(value));
  }

  filteredRepayments(): LoanRepaymentRow[] {
    const value = this.search().trim().toLowerCase();
    return this.repayments().filter(row =>
      !value ||
      row.idempotencyKey.toLowerCase().includes(value) ||
      this.repaymentStatusLabel(row).toLowerCase().includes(value));
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
  }

  openCreateDialog(): void {
    this.createModel = {
      customerId: '',
      externalReference: '',
      idempotencyKey: '',
      principalAmount: 0,
      annualRatePercent: 12,
      termMonths: 12,
      currencyCode: 'IRR'
    };
    this.actionError.set(null);
    this.createDialogVisible = true;
  }

  createLoan(): void {
    const payload = {
      customerId: this.createModel.customerId.trim(),
      externalReference: this.createModel.externalReference.trim(),
      idempotencyKey: this.createModel.idempotencyKey.trim(),
      principalAmount: Number(this.createModel.principalAmount),
      annualRatePercent: Number(this.createModel.annualRatePercent),
      termMonths: Number(this.createModel.termMonths),
      currencyCode: this.createModel.currencyCode.trim()
    };

    if (!payload.customerId || !payload.externalReference || !payload.idempotencyKey || payload.principalAmount <= 0 || payload.termMonths <= 0 || !payload.currencyCode) {
      return;
    }

    this.creating.set(true);
    this.api.createLoan(payload).subscribe({
      next: created => {
        this.loans.update(rows => [created, ...rows]);
        this.resolveLoanCustomers([created]);
        this.creating.set(false);
        this.createDialogVisible = false;
      },
      error: () => {
        this.creating.set(false);
        this.actionError.set(this.t('payments.lending.errorCreate'));
      }
    });
  }

  repaymentStatusLabel(row: LoanRepaymentRow): string {
    if (!row.postingErrorCode) {
      return this.t('payments.common.posted');
    }

    return row.postingErrorCode;
  }

  statusLabel(status: string): string {
    const key = paymentStatusLabelKey(status);
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  statusSeverity(status: string): 'success' | 'warn' | 'danger' | 'info' {
    const normalized = status.toLowerCase();
    if (normalized.includes('fail') || normalized.includes('cancel') || normalized.includes('default')) {
      return 'danger';
    }
    if (normalized.includes('pending') || normalized.includes('draft')) {
      return 'warn';
    }
    if (normalized.includes('active') || normalized.includes('approve') || normalized.includes('paid')) {
      return 'success';
    }
    return 'info';
  }

  customerLabel(customerId: string): string {
    const customer = this.platformCustomer(customerId);
    return customer?.displayName ?? customerId;
  }

  customerCode(customerId: string): string {
    const customer = this.platformCustomer(customerId);
    return customer?.customerCode ? `#${customer.customerCode}` : '';
  }

  customerStatus(customerId: string): 'active' | 'inactive' | 'missing' {
    const customer = this.platformCustomer(customerId);
    if (!customer) {
      return 'missing';
    }

    const status = String(customer.status).toLowerCase();
    return status === 'active' ? 'active' : 'inactive';
  }

  customerStatusLabel(customerId: string): string {
    const status = this.customerStatus(customerId);
    if (status === 'active') {
      return this.t('payments.common.enabled');
    }
    if (status === 'inactive') {
      return this.t('payments.common.disabled');
    }
    return this.t('payments.common.missing');
  }

  customerStatusSeverity(customerId: string): 'success' | 'warn' | 'danger' {
    const status = this.customerStatus(customerId);
    if (status === 'active') {
      return 'success';
    }
    if (status === 'inactive') {
      return 'warn';
    }
    return 'danger';
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }
}
